import React, { useContext, useEffect, useMemo, useState } from 'react';
import userPermissionContext from '../../../context/userPermissionContext';
import { KTSVG } from '../../../_metronic/helpers';

const API_URL = process.env.REACT_APP_API_URL || '';

const toNum = (v, d = 0) => {
	const n = Number(v);
	return Number.isFinite(n) ? n : d;
};

const sumObjectValues = (obj) => {
	if (!obj || typeof obj !== 'object') return 0;
	return Object.values(obj).reduce((acc, v) => acc + toNum(v, 0), 0);
};

const deepNumericSum = (value) => {
	if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
	if (typeof value === 'string') return toNum(value, 0);
	if (Array.isArray(value)) return value.reduce((acc, v) => acc + deepNumericSum(v), 0);
	if (value && typeof value === 'object') return Object.values(value).reduce((acc, v) => acc + deepNumericSum(v), 0);
	return 0;
};

const extractArray = (payload, preferredKey) => {
	if (!payload) return [];
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload?.data)) return payload.data;
	if (preferredKey && Array.isArray(payload?.[preferredKey])) return payload[preferredKey];
	if (preferredKey && Array.isArray(payload?.data?.[preferredKey])) return payload.data[preferredKey];
	const firstArray = Object.values(payload || {}).find((v) => Array.isArray(v));
	if (Array.isArray(firstArray)) return firstArray;
	if (payload?.data && typeof payload.data === 'object') {
		const nestedFirst = Object.values(payload.data).find((v) => Array.isArray(v));
		if (Array.isArray(nestedFirst)) return nestedFirst;
	}
	return [];
};

function ManagementDashboard() {
	const { jwt, businessUnit } = useContext(userPermissionContext);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState('');
	const [liveRows, setLiveRows] = useState([]);
	const [aggExpiryExpired, setAggExpiryExpired] = useState({});
	const [aggExpirySoon, setAggExpirySoon] = useState({});
	const [headWiseCost, setHeadWiseCost] = useState([]);
	const [expenseSummary, setExpenseSummary] = useState({});
	const [projectVehicle, setProjectVehicle] = useState([]);
	const [categoryVehicle, setCategoryVehicle] = useState([]);
	const [lastSync, setLastSync] = useState(null);

	useEffect(() => {
		let cancelled = false;
		const token = jwt?.token;
		if (!token) return undefined;

		const authHeaders = { Authorization: `Bearer ${token}` };
		const qp = businessUnit?.idProjects ? `?currentProject=${businessUnit.idProjects}` : '';
		const trackerToken = localStorage.getItem('TrackerToken');

		const fetchJson = (url, init = {}) =>
			fetch(url, init).then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))));

		const load = async () => {
			try {
				setIsLoading(true);
				setError('');
				const requests = [
					fetchJson(`${API_URL}vms_app_dashboard_total_count${qp}`, { headers: authHeaders }),
					fetchJson(`${API_URL}vms_app_dashboard_expiration_count${qp}${qp ? '&' : '?'}documentAlertRange=expired`, { headers: authHeaders }),
					fetchJson(`${API_URL}vms_app_dashboard_expiration_count${qp}${qp ? '&' : '?'}documentAlertRange=30`, { headers: authHeaders }),
					fetchJson(`${API_URL}vms_app_dashboard_expense_head_total_cost${qp}`, { headers: authHeaders }),
					fetchJson(`${API_URL}vms_app_expense_cost_summary${qp}`, { headers: authHeaders }),
					// NOTE: `vms_app_vehicle_info_by_project` returns summary + category split,
					// not project rows. For this table we need the dedicated project-wise API.
					fetchJson(`${API_URL}vms_app_project_wise_vehicle${qp}`, { headers: authHeaders }),
				];
				if (trackerToken) {
					requests.push(
						fetchJson(`${API_URL}liveTracData`, {
							method: 'POST',
							headers: { ...authHeaders, 'Content-Type': 'application/json' },
							body: JSON.stringify({ tracker_token: trackerToken }),
						})
					);
				}

				const res = await Promise.allSettled(requests);
				if (cancelled) return;

				const pick = (idx) => (res[idx]?.status === 'fulfilled' ? res[idx].value : null);
				const rTotal = pick(0);
				const rExpiry = pick(1);
				const rExpirySoon = pick(2);
				const rHead = pick(3);
				const rSummary = pick(4);
				const rProject = pick(5);
				const rLive = pick(6);

				const totalPayload = rTotal?.data || rTotal || {};
				setAggExpiryExpired(rExpiry?.data || rExpiry || {});
				setAggExpirySoon(rExpirySoon?.data || rExpirySoon || {});
				// API shape is:
				// { success:true, data:{ totalCost, expenseHeadWiseTotalCost:[...], ... } }
				setHeadWiseCost(extractArray(rHead, 'expenseHeadWiseTotalCost'));
				setExpenseSummary(rSummary?.data || rSummary || {});
				// Prefer dedicated project-wise endpoint, fallback to dashboard-total payload.
				const projectRowsPrimary = extractArray(rProject, 'projectWiseVehicle');
				const projectRowsFallback = extractArray(totalPayload, 'projectWiseVehicleCount');
				const projectRows =
					Array.isArray(projectRowsPrimary) && projectRowsPrimary.length > 0
						? projectRowsPrimary
						: projectRowsFallback;
				setProjectVehicle(projectRows);
				setCategoryVehicle(extractArray(totalPayload, 'categoryWiseVehicleCount'));
				setLiveRows(Array.isArray(rLive?.data) ? rLive.data : []);
				setLastSync(new Date());
				const failed = res.filter((r) => r.status === 'rejected').length;
				if (failed > 0 && !cancelled) {
					setError(`Loaded with partial data (${failed} source${failed > 1 ? 's' : ''} failed).`);
				}
			} catch (e) {
				if (!cancelled) setError('Failed to load management dashboard data.');
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};

		load();
		return () => {
			cancelled = true;
		};
	}, [jwt?.token, businessUnit?.idProjects]);

	const liveKpi = useMemo(() => {
		const total = liveRows.length;
		let moving = 0;
		let idle = 0;
		let stopped = 0;
		let overspeed = 0;
		liveRows.forEach((item) => {
			const speed = toNum(item?.speed, 0);
			const stopTxt = String(item?.stop_duration || '');
			const stopMin =
				toNum((stopTxt.match(/(\d+)\s*h/) || [])[1], 0) * 60 + toNum((stopTxt.match(/(\d+)\s*min/) || [])[1], 0);
			if (speed > 80) overspeed += 1;
			if (speed > 3) moving += 1;
			else if (stopMin > 30) stopped += 1;
			else idle += 1;
		});
		return {
			total,
			moving,
			idle,
			stopped,
			overspeed,
			utilizationPct: total > 0 ? Math.round((moving / total) * 100) : 0,
		};
	}, [liveRows]);

	const expiryExpiredTotal = useMemo(() => {
		const explicit =
			toNum(aggExpiryExpired?.taxTokenExpirationCount, 0) +
			toNum(aggExpiryExpired?.fitnessExpirationCount, 0) +
			toNum(aggExpiryExpired?.routePermitExpirationCount, 0) +
			toNum(aggExpiryExpired?.insuranceExpirationCount, 0);
		return explicit > 0 ? explicit : deepNumericSum(aggExpiryExpired);
	}, [aggExpiryExpired]);
	const expirySoonTotal = useMemo(() => {
		const explicit =
			toNum(aggExpirySoon?.taxTokenExpirationCount, 0) +
			toNum(aggExpirySoon?.fitnessExpirationCount, 0) +
			toNum(aggExpirySoon?.routePermitExpirationCount, 0) +
			toNum(aggExpirySoon?.insuranceExpirationCount, 0);
		return explicit > 0 ? explicit : deepNumericSum(aggExpirySoon);
	}, [aggExpirySoon]);
	const expiryTotal = expiryExpiredTotal + expirySoonTotal;
	const costTotal = useMemo(() => sumObjectValues(expenseSummary), [expenseSummary]);

	return (
		<div className='card card-custom'>
			<div className='card-header py-4 d-flex justify-content-between'>
				<div className='card-title'>
					<KTSVG path='/media/svg/icons/Layout/Layout-4-blocks.svg' className='svg-icon-1 svg-icon-primary' />
					<span className='fw-bolder ms-2'>MANAGEMENT REPORTING DASHBOARD</span>
				</div>
				<div className='small text-muted d-flex align-items-center'>
					{lastSync ? `Last sync: ${lastSync.toLocaleTimeString()}` : 'Syncing...'}
				</div>
			</div>
			<div className='card-body'>
				{error && <div className='alert alert-danger py-2'>{error}</div>}

				<div className='row g-3 mb-4'>
					{[
						{ label: 'Fleet (Live)', value: liveKpi.total, tone: '#1e40af', href: '/vehicleList' },
						{ label: 'Moving', value: liveKpi.moving, tone: '#047857' },
						{ label: 'Idle', value: liveKpi.idle, tone: '#b45309' },
						{ label: 'Stopped', value: liveKpi.stopped, tone: '#b91c1c' },
						{ label: 'Overspeed', value: liveKpi.overspeed, tone: liveKpi.overspeed > 0 ? '#be123c' : '#64748b' },
						{ label: 'Utilization', value: `${liveKpi.utilizationPct}%`, tone: '#6d28d9' },
						{ label: 'Compliance Risk Items', value: expiryTotal, tone: expiryTotal > 0 ? '#c2410c' : '#047857', href: '/vehicleList' },
						{ label: 'Expired Docs', value: expiryExpiredTotal, tone: expiryExpiredTotal > 0 ? '#b91c1c' : '#047857' },
						{ label: 'Due in 30 Days', value: expirySoonTotal, tone: expirySoonTotal > 0 ? '#b45309' : '#047857' },
						{ label: 'Cost Summary Total', value: costTotal.toLocaleString(), tone: '#0f766e', href: '/report-operatingCostSummaryReport' },
					].map((kpi, idx) => (
						<div className='col-6 col-md-4 col-xl-3' key={`${kpi.label}-${idx}`}>
							<div
								className='rounded p-3 h-100'
								style={{
									background: '#ffffff',
									border: '1px solid #e9edf3',
									borderTop: `4px solid ${kpi.tone}`,
								}}
							>
								<div className='small text-uppercase fw-bold' style={{ color: '#6b7280', letterSpacing: 0.4 }}>{kpi.label}</div>
								<div className='fs-2 fw-bolder' style={{ color: kpi.tone }}>{isLoading ? '...' : kpi.value}</div>
								{kpi.href && (
									<div style={{ marginTop: 6 }}>
										<a href={`#${kpi.href}`} style={{ fontSize: 11, fontWeight: 700, color: kpi.tone, textDecoration: 'none' }}>
											View details →
										</a>
									</div>
								)}
							</div>
						</div>
					))}
				</div>

				<div className='row g-3'>
					<div className='col-lg-6'>
						<div className='border rounded h-100 overflow-hidden'>
							<div className='px-3 py-2 fw-bold text-white d-flex justify-content-between align-items-center' style={{ background: 'linear-gradient(135deg,#0ea5e9 0%,#2563eb 100%)' }}>
								<span>Expense Head Wise Cost</span>
								<a href='#/report-operatingCostSummaryReport' style={{ color: '#fff', fontSize: 12, textDecoration: 'none' }}>
									Open report →
								</a>
							</div>
							<div style={{ maxHeight: 320, overflowY: 'auto' }}>
								<table className='table table-sm align-middle mb-0'>
									<thead>
										<tr style={{ background: '#eff6ff' }}>
											<th>Head</th>
											<th className='text-end'>Amount</th>
										</tr>
									</thead>
									<tbody>
										{(headWiseCost || []).map((r, i) => (
											<tr key={`h-${i}`}>
												<td>{r?.head_name || r?.headName || r?.expenseHead || r?.expenseTypeName || '-'}</td>
												<td className='text-end'>
													{toNum(
														r?.totalCost ??
															r?.total ??
															r?.amount ??
															r?.cost ??
															r?.expenseAmount,
														0
													).toLocaleString()}
												</td>
											</tr>
										))}
										{!isLoading && (!headWiseCost || headWiseCost.length === 0) && (
											<tr>
												<td colSpan={2} className='text-muted text-center py-3'>
													No data
												</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>

					<div className='col-lg-6'>
						<div className='border rounded h-100 overflow-hidden'>
							<div className='px-3 py-2 fw-bold text-white d-flex justify-content-between align-items-center' style={{ background: 'linear-gradient(135deg,#16a34a 0%,#15803d 100%)' }}>
								<span>Project Wise Vehicle Snapshot</span>
								<a href='#/report-buWiseLightVehicle' style={{ color: '#fff', fontSize: 12, textDecoration: 'none' }}>
									Open report →
								</a>
							</div>
							<div style={{ maxHeight: 320, overflowY: 'auto' }}>
								<table className='table table-sm align-middle mb-0'>
									<thead>
										<tr style={{ background: '#ecfdf5' }}>
											<th>Project</th>
											<th className='text-end'>Vehicles</th>
										</tr>
									</thead>
									<tbody>
										{(projectVehicle || []).map((r, i) => (
											<tr key={`p-${i}`}>
												<td>{r?.project_name || r?.projectName || r?.name || '-'}</td>
												<td className='text-end'>
													{toNum(
														r?.totalActiveVehicles ??
															r?.totalVehicle ??
															r?.totalVehicles ??
															r?.vehicle_count ??
															r?.count ??
															r?.total,
														0
													)}
												</td>
											</tr>
										))}
										{!isLoading && (!projectVehicle || projectVehicle.length === 0) && (
											<tr>
												<td colSpan={2} className='text-muted text-center py-3'>
													No data
												</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>

				<div className='row g-3 mt-1'>
					<div className='col-lg-6'>
						<div className='border rounded h-100 overflow-hidden'>
							<div className='px-3 py-2 fw-bold text-white d-flex justify-content-between align-items-center' style={{ background: 'linear-gradient(135deg,#f59e0b 0%,#d97706 100%)' }}>
								<span>Category Wise Vehicle Count</span>
								<a href='#/report-typeWiseVehicle' style={{ color: '#fff', fontSize: 12, textDecoration: 'none' }}>
									Open report →
								</a>
							</div>
							<div style={{ maxHeight: 320, overflowY: 'auto' }}>
								<table className='table table-sm align-middle mb-0'>
									<thead>
										<tr style={{ background: '#fffbeb' }}>
											<th>Category</th>
											<th className='text-end'>Vehicles</th>
										</tr>
									</thead>
									<tbody>
										{(categoryVehicle || []).map((r, i) => (
											<tr key={`c-${i}`}>
												<td>{r?.categoryName || r?.category_name || r?.name || 'Others'}</td>
												<td className='text-end'>
													{toNum(
														r?.totalActiveVehicles ??
															r?.totalVehicle ??
															r?.totalVehicles ??
															r?.count ??
															r?.total,
														0
													)}
												</td>
											</tr>
										))}
										{!isLoading && (!categoryVehicle || categoryVehicle.length === 0) && (
											<tr>
												<td colSpan={2} className='text-muted text-center py-3'>
													No data
												</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default ManagementDashboard;
